import { API_URL } from '@/utils/constants'
import { twetchJson, TWETCH_WEB, type TwetchUser } from './twetch-api'
import { useAlkanes } from './store'
import { primeIdentity } from './useTwetchIdentities'

/// The wallet a Twetch user actually trades from, remembered per session.
///
/// A user can have several Bitcoin addresses on Twetch: the root key their
/// account was created with (served on the profile as `btcAddress`) and any
/// LINKED wallets — which is where an OW extension wallet attached to an
/// older account lives, and where the alkane trading happens. Opening the
/// root key would land on a dormant wallet, so every candidate is asked for
/// its alkane history and the busiest one wins; the root key is only the
/// fallback when nothing else has a story to tell.
const chosen = new Map<number, Promise<string | null>>()

interface AddressRow {
	address: string
	root: boolean
}

async function alkaneRows(address: string): Promise<number> {
	try {
		const res = await fetch(`${API_URL}/alkane/address/${address}/history`)
		if (!res.ok) return 0
		const body = (await res.json()) as { rows?: unknown[]; pending?: unknown[] }
		return (body.rows?.length ?? 0) + (body.pending?.length ?? 0)
	} catch {
		return 0
	}
}

async function candidates(userId: number): Promise<AddressRow[]> {
	try {
		const body = await twetchJson<{ addresses?: AddressRow[] }>(`/v1/users/${userId}/addresses`)
		if (Array.isArray(body.addresses) && body.addresses.length > 0) {
			return body.addresses
		}
	} catch {
		// An API that predates the endpoint: fall through to the profile's
		// root key, which every deploy serves.
	}
	const u = await twetchJson<{ btcAddress?: string | null }>(`/v1/users/${userId}`)
	return u.btcAddress ? [{ address: u.btcAddress, root: true }] : []
}

function tradingWalletOf(userId: number): Promise<string | null> {
	let p = chosen.get(userId)
	if (!p) {
		p = (async () => {
			const [rows, user] = await Promise.all([
				candidates(userId),
				twetchJson<TwetchUser>(`/v1/users/${userId}`).catch(() => null)
			])
			if (rows.length === 0) return null
			// Busiest wallet wins; ties keep the API's order (linked newest
			// first, root last), which is also the better default.
			const counts = await Promise.all(rows.map((r) => alkaneRows(r.address)))
			let best = 0
			for (let i = 1; i < rows.length; i += 1) {
				if (counts[i] > counts[best]) best = i
			}
			const addr = rows[best].address
			if (user) {
				// Every candidate wears the identity — the batch index does not
				// know legacy root keys, and the profile must not show a bare
				// address for someone we just clicked by name.
				for (const r of rows) {
					primeIdentity(r.address, {
						id: user.id,
						name: user.name,
						icon: user.icon,
						verifiedIcon: user.verifiedIcon
					})
				}
			}
			return addr
		})().catch(() => {
			chosen.delete(userId)
			return null
		})
		chosen.set(userId, p)
	}
	return p
}

/// Clicking an author anywhere in the app opens THEIR profile here — the
/// same canonical `/alkanes/profile/:address` page the swap tape and holder
/// rows open — at the wallet they trade from. A user with no Bitcoin address
/// on record still has a Twetch page; that opens instead of a dead end.
export async function openTwetchUserProfile(userId: number): Promise<void> {
	const addr = await tradingWalletOf(userId)
	if (addr) {
		// The terminal routes through its store; any other page (a collection's
		// social tab) has no terminal mounted, so it navigates there for real.
		if (window.location.pathname.startsWith('/alkanes')) {
			useAlkanes.getState().openProfile(addr)
		} else {
			window.location.assign(`/alkanes/profile/${encodeURIComponent(addr)}`)
		}
		return
	}
	window.open(`${TWETCH_WEB}/u/${userId}`, '_blank', 'noopener,noreferrer')
}
